import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardTextIcon, MapPinIcon, FloppyDiskIcon, CheckCircleIcon, CameraIcon, XIcon } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DRAFT_KEY = "chargesure_audit_draft";
const CONNECTORS = ["CCS2", "Type2", "CHAdeMO", "GB/T", "Bharat AC001", "Bharat DC001"];

const blank = {
  site_id: "", site_reachable: true, chargers_present: true, chargers_operational: true,
  operator_name_matches: true, connector_types_observed: [], working_connectors: 0,
  total_connectors: 0, signage_present: false, parking_accessible: false,
  tariff_displayed: "", notes: "", observed_status: "AVAILABLE", gps: null,
};

function Toggle({ label, value, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!value)}
      className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left text-sm transition-colors ${value ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
      <span className="font-medium text-slate-700">{label}</span>
      <span className={`relative h-6 w-11 rounded-full transition-colors ${value ? "bg-emerald-500" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}

export default function Audit() {
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef();

  const [params] = useSearchParams();
  const [pinned, setPinned] = useState(null);

  useEffect(() => {
    api.get("/sites", { params: { limit: 1000 } }).then((r) => setSites(r.data));
    const d = localStorage.getItem(DRAFT_KEY);
    if (d) { try { setForm({ ...blank, ...JSON.parse(d) }); } catch (e) {} }
  }, []);

  useEffect(() => {
    const sid = params.get("site");
    if (!sid) return;
    api.get(`/sites/${sid}`).then((r) => {
      const st = r.data.site;
      setPinned({ id: st.id, name: st.name });
      setForm((f) => ({ ...f, site_id: st.id }));
    }).catch(() => {});
  }, [params]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveDraft = () => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    setSavedAt(new Date());
    toast.success("Draft saved locally");
  };

  const captureGps = () => {
    if (!navigator.geolocation) return toast.error("Geolocation unavailable");
    navigator.geolocation.getCurrentPosition(
      (pos) => { set("gps", { lat: pos.coords.latitude, lng: pos.coords.longitude }); toast.success("GPS captured"); },
      () => toast.error("Could not get location")
    );
  };

  const toggleConnector = (c) => {
    const has = form.connector_types_observed.includes(c);
    set("connector_types_observed", has ? form.connector_types_observed.filter((x) => x !== c) : [...form.connector_types_observed, c]);
  };

  const onPickPhotos = async (files) => {
    setUploadingPhoto(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      if (form.gps) { fd.append("lat", form.gps.lat); fd.append("lng", form.gps.lng); }
      try {
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data]);
      } catch (e) { toast.error("Photo upload failed"); }
    }
    setUploadingPhoto(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.site_id) return toast.error("Select a site");
    setSubmitting(true);
    try {
      const payload = { ...form, tariff_displayed: form.tariff_displayed === "" ? null : parseFloat(form.tariff_displayed),
        working_connectors: parseInt(form.working_connectors) || 0, total_connectors: parseInt(form.total_connectors) || 0,
        photo_ids: photos.map((p) => p.id) };
      await api.post("/audits", payload);
      toast.success("Audit submitted — confidence recalculated");
      localStorage.removeItem(DRAFT_KEY);
      setForm(blank); setPhotos([]);
    } catch (err) { toast.error(err.response?.data?.detail || "Submit failed"); } finally { setSubmitting(false); }
  };

  return (
    <div className="p-5 lg:p-8 max-w-2xl mx-auto lg:mx-0">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardTextIcon size={26} weight="duotone" className="text-emerald-600" />
        <h1 className="font-display text-2xl lg:text-3xl font-semibold text-slate-900">Field Audit</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">Connectivity-tolerant checklist. Drafts save to this device automatically.</p>

      <form onSubmit={submit} className="space-y-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Site</label>
          <select data-testid="audit-site" value={form.site_id} onChange={(e) => set("site_id", e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-3 text-sm outline-none focus:border-emerald-500">
            <option value="">Select a site to audit…</option>
            {pinned && !sites.some((s) => s.id === pinned.id) && <option value={pinned.id}>{pinned.name}</option>}
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button type="button" data-testid="capture-gps" onClick={captureGps} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors">
            <MapPinIcon size={16} /> {form.gps ? `GPS ✓ ${form.gps.lat.toFixed(4)}, ${form.gps.lng.toFixed(4)}` : "Capture GPS"}
          </button>
        </div>

        <div className="space-y-2">
          <Toggle testid="chk-reachable" label="Site reachable / exists" value={form.site_reachable} onChange={(v) => set("site_reachable", v)} />
          <Toggle testid="chk-present" label="Chargers physically present" value={form.chargers_present} onChange={(v) => set("chargers_present", v)} />
          <Toggle testid="chk-operational" label="Chargers operational" value={form.chargers_operational} onChange={(v) => set("chargers_operational", v)} />
          <Toggle testid="chk-operator" label="Operator name matches listing" value={form.operator_name_matches} onChange={(v) => set("operator_name_matches", v)} />
          <Toggle testid="chk-signage" label="Signage present" value={form.signage_present} onChange={(v) => set("signage_present", v)} />
          <Toggle testid="chk-parking" label="Parking accessible" value={form.parking_accessible} onChange={(v) => set("parking_accessible", v)} />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Connectors observed</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {CONNECTORS.map((c) => (
              <button key={c} type="button" onClick={() => toggleConnector(c)}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${form.connector_types_observed.includes(c) ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>{c}</button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Working connectors</label>
              <input data-testid="working-connectors" type="number" min="0" value={form.working_connectors} onChange={(e) => set("working_connectors", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Total connectors</label>
              <input type="number" min="0" value={form.total_connectors} onChange={(e) => set("total_connectors", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">Observed status</label>
            <select value={form.observed_status} onChange={(e) => set("observed_status", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-500">
              <option>AVAILABLE</option><option>CHARGING</option><option>OUTOFORDER</option><option>unknown</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Tariff displayed (₹/kWh)</label>
            <input data-testid="tariff-displayed" type="number" step="0.1" value={form.tariff_displayed} onChange={(e) => set("tariff_displayed", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" placeholder="e.g. 18.5" />
          </div>
          <textarea data-testid="audit-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes…" rows={3} className="sm:col-span-2 rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><CameraIcon size={14} /> Photo evidence (private · stored with GPS + timestamp)</label>
          <input ref={photoRef} type="file" accept="image/*" multiple hidden onChange={(e) => onPickPhotos(e.target.files)} data-testid="audit-photo-input" />
          <div className="mt-3 grid grid-cols-4 gap-2">
            {photos.map((p) => (
              <div key={p.id} className="relative aspect-square overflow-hidden rounded-lg border border-slate-200">
                <img src={`${BACKEND}${p.url}`} alt={p.filename} className="h-full w-full object-cover" />
                <button type="button" onClick={() => setPhotos((ps) => ps.filter((x) => x.id !== p.id))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><XIcon size={12} /></button>
              </div>
            ))}
            <button type="button" data-testid="audit-add-photo" onClick={() => photoRef.current?.click()} disabled={uploadingPhoto}
              className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-emerald-400 hover:text-emerald-600 transition-colors disabled:opacity-50">
              <CameraIcon size={20} /><span className="text-[10px]">{uploadingPhoto ? "Uploading…" : "Add"}</span>
            </button>
          </div>
        </div>

        <div className="flex gap-3 sticky bottom-16 lg:bottom-0 bg-slate-50 py-2">
          <button type="button" data-testid="save-draft" onClick={saveDraft} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-white transition-colors">
            <FloppyDiskIcon size={16} /> Save draft
          </button>
          <button type="submit" data-testid="submit-audit" disabled={submitting} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-60">
            <CheckCircleIcon size={18} weight="fill" /> {submitting ? "Submitting…" : "Submit audit"}
          </button>
        </div>
        {savedAt && <div className="text-xs text-slate-400">Draft saved at {savedAt.toLocaleTimeString()}</div>}
      </form>
    </div>
  );
}
